import { useEffect, useState } from "react";
import {
  Typography,
  Button,
  AppBar,
  IconButton,
  Toolbar,
  DialogContent,
  Stack,
  Box,
  FormHelperText,
} from "@mui/material";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  PersonRemove as DissociateIcon,
} from "@mui/icons-material";
import {
  prettify,
  renderDeviceName,
  renderEUDateTime,
  toastError,
} from "../../../utils/utils";
import { validateDeviceForm } from "../deviceFormUtils";
import { useAuthUser } from "react-auth-kit";
import { LoadingError, LoadingPaper } from "../../LoadingComponents";
import { EditableInputDisplay, useEditMode } from "../../EditableComponents";

import {
  useDissociateFromPatient,
  useGetDeviceById,
  usePutDeviceById,
} from "..";
import DeviceAssociationFields from "../DeviceAssociationFields/DeviceAssociationFields";

function ViewDevice({ device_id, closeModal, _editMode }) {
  const authUser = useAuthUser();
  const isAssignOnly =
    authUser()?.role === 2 || authUser()?.role === 3;
  const canEditOrg = authUser()?.role === 1;

  const queryClient = useQueryClient();
  const deviceQuery = useGetDeviceById(device_id);
  const editMutation = usePutDeviceById(device_id);
  const dissociateMutation = useDissociateFromPatient();

  const [formErrors, setFormErrors] = useState({});

  const {
    editMode,
    enterEditMode,
    confirmEditMode,
    updateEditState,
    updateEditStateMany,
  } = useEditMode({
    editModeEnabled: _editMode || isAssignOnly,
    updateMutation: editMutation,
    initialQueryKey: ["devices", device_id],
  });

  useEffect(() => {
    if (_editMode || isAssignOnly) enterEditMode(true);
  }, [_editMode, isAssignOnly]);

  const handleDissociateFromPatient = () => {
    dissociateMutation.mutate(deviceQuery.data);
  };

  const handleFieldChange = (id, val) => {
    setFormErrors({});
    updateEditState(id, val);
  };

  const handleFieldsChange = (patch) => {
    setFormErrors({});
    updateEditStateMany(patch);
  };

  const handleConfirmEdit = () => {
    const current =
      queryClient.getQueryData(["devices", device_id]) ?? deviceQuery.data;
    const { valid, errors, message } = validateDeviceForm(current, {
      requireSerialNumber: !isAssignOnly,
      requireAssociation: canEditOrg,
    });

    if (!valid) {
      setFormErrors(errors);
      toast.error(message, toastError);
      return;
    }

    setFormErrors({});
    confirmEditMode();
  };

  const device =
    queryClient.getQueryData(["devices", device_id]) ?? deviceQuery.data;
  const associationEditMode = isAssignOnly || editMode;

  return (
    <>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={closeModal}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      {deviceQuery.isLoading ? (
        <LoadingPaper />
      ) : deviceQuery.isError ? (
        <LoadingError error={deviceQuery.error} />
      ) : (
        <DialogContent>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h5" component="div">
            {isAssignOnly
              ? `Gestisci dispositivo: ${prettify(deviceQuery.data.name)}`
              : `Visualizza dispositivo: ${prettify(deviceQuery.data.name)}`}
          </Typography>

          <Stack spacing={2} direction={"column"}>
            <Box sx={{ width: "100%" }}>
              <Box sx={{ float: "left" }}>
                <Button
                  style={{ margin: "5px" }}
                  color={editMode ? "success" : "warning"}
                  variant="contained"
                  onClick={
                    isAssignOnly
                      ? handleConfirmEdit
                      : editMode
                        ? handleConfirmEdit
                        : () => {
                            setFormErrors({});
                            enterEditMode(true);
                          }
                  }
                >
                  <EditIcon sx={{ marginRight: "5px" }} />
                  {editMode ? "Conferma" : "Modifica"}
                </Button>

                <Button
                  style={{ margin: "5px" }}
                  variant="contained"
                  disabled={deviceQuery.data.patient_id === null}
                  onClick={handleDissociateFromPatient}
                >
                  <DissociateIcon sx={{ marginRight: "5px" }} />
                  Dissocia paziente
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                margin: "auto",
                gridTemplateColumns: isAssignOnly ? "1fr" : "1fr 1fr",
                width: "100%",
              }}
            >
              {!isAssignOnly && (
                <Box sx={{ margin: "10px" }}>
                  <Typography>
                    <strong>Tipo di dispositivo:</strong>{" "}
                    {renderDeviceName(deviceQuery.data.device_type)}
                  </Typography>
                  <Typography>
                    <strong>Stato batteria:</strong>{" "}
                    {deviceQuery.data.battery_perc}
                    {deviceQuery.data.battery_perc !== null && "%"}
                  </Typography>
                  <Typography>
                    <strong>Ultima connessione:</strong>{" "}
                    {renderEUDateTime(deviceQuery.data.last_connection)}
                  </Typography>
                  <EditableInputDisplay
                    id={"serial_number"}
                    label={"Numero seriale:"}
                    initialValue={deviceQuery.data.serial_number}
                    setEditMode={editMode}
                    inputMode={"text"}
                    setParentValue={handleFieldChange}
                  />
                  {formErrors.serial_number && (
                    <FormHelperText error sx={{ ml: "50%" }}>
                      {formErrors.serial_number}
                    </FormHelperText>
                  )}
                  <EditableInputDisplay
                    id={"note"}
                    label={"Note:"}
                    initialValue={deviceQuery.data.note}
                    setEditMode={editMode}
                    inputMode={"text"}
                    setParentValue={handleFieldChange}
                  />
                </Box>
              )}

              <Box sx={{ margin: "10px" }}>
                <DeviceAssociationFields
                  orgId={device?.org_id}
                  patientId={device?.patient_id}
                  kitId={device?.kit_id}
                  editMode={associationEditMode}
                  canEditOrg={canEditOrg}
                  onFieldsChange={handleFieldsChange}
                  associationError={formErrors.association}
                />

                <EditableInputDisplay
                  id={"is_online"}
                  label={"Stato (online)"}
                  initialValue={deviceQuery.data.is_online}
                  setEditMode={isAssignOnly ? true : editMode}
                  inputMode={"bool"}
                  setParentValue={handleFieldChange}
                />
                <EditableInputDisplay
                  id={"is_installed"}
                  label={"Stato (installato)"}
                  initialValue={deviceQuery.data.is_installed}
                  setEditMode={isAssignOnly ? true : editMode}
                  inputMode={"bool"}
                  setParentValue={handleFieldChange}
                />

                {!isAssignOnly && (
                  <EditableInputDisplay
                    id={"name"}
                    label={"Descrizione:"}
                    initialValue={deviceQuery.data.name}
                    setEditMode={editMode}
                    inputMode={"text"}
                    setParentValue={handleFieldChange}
                  />
                )}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      )}
    </>
  );
}

export default ViewDevice;
